import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { ZSwitch } from '../../../../../../widgets/zswitch/zswitch';
import { Tab, TabList, TabPanel } from '../../../../../../widgets/ztabs';
import React, { FC } from 'react';
import Select from 'react-select';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { T } from 'src/shared/helpers';
import Plus from 'src/shared/material_icons/content/add.svg';
import AttributeIcon from 'src/shared/material_icons/image/tune.svg';
import styled from 'styled-components';
import { ComposerOptionTreeElement } from '../../../configurator-composer/composer/components/composerOptionTreeElement';
import Composer3DPreviewCapture from '../../../configurator-composer/composer/components/composer3DPreviewCapture';
import ComposerValidationTooltip from '../../../configurator-composer/composer/components/composerValidationTooltip';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import ComposerRetargetAttributeDialog from '../../../configurator-composer/composer/dialogs/composerRetargetAttributeDialog';
import {
    getRetargetObjectsAndMaterials,
    randomCode,
    retargetMapToActions,
    sortByDisplay
} from '../../../configurator-composer/composer/helpers/composerHelpers';
import { isMacPlatform } from '../../../configurator-composer/composer/helpers/composerSelectionHint';
import { createComposerLocalCode, isComposerStoreCode } from '../../../configurator-composer/composer/helpers/composerLocalCode';
import { COMPOSER_OPTION_DRAG_TYPE, reorderComposerOptions } from '../../../configurator-composer/composer/helpers/composerTreeDnD';
import { applyComposerOptionImage, createComposerOptionImageReference } from '../../../configurator-composer/composer/helpers/composerOptionImage';
import type { TreeNodeHook } from '../../tree';
import {
    ComposerDetailValue,
    ComposerEmptyState,
    ComposerFormStack,
    ComposerIconButton,
    ComposerKindBadge,
    ComposerList,
    ComposerPanel,
    ComposerScrollBody,
    ComposerSearch,
    ComposerSearchRow,
    ComposerSubjectContent,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectMeta,
    ComposerSubjectNameInput,
    ComposerTabCount,
    ComposerTabs,
    ComposerToolbar,
    ComposerToolbarButton,
    ComposerToolbarSpacer,
    ComposerUnsupportedNote
} from '../composerWorkspace';
import { ComposerAttributeNode, ComposerAttributeOptionNode, ComposerAttributePreviewType } from './composerAttributesModels';
import ComposerLinksEditor from './composerLinksEditor';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';

const TabBody = styled.div`
    height: 100%;
    overflow: auto;
    padding: 8px;
`;

const ListHint = styled.p`
    margin: 10px 2px 0;
    color: var(--composer-faint, #677078);
    font-size: 11px;
    line-height: 16px;
`;

const ComposerOptions: FC<{
    selectedAttribute: ComposerAttributeNode | null;
    options: ComposerAttributeOptionNode[];
    multiple: boolean;
    state: TreeNodeHook<ComposerAttributeOptionNode>;
}> = ({ selectedAttribute, options, multiple, state }) => {
    const [
        attributes,
        sceneMaterialList,
        sceneMeshList,
        updateAttribute,
        updateOption,
        removeOption,
        cloneOption,
        selectOptions,
        validateAttributes,
        validateOptions,
        selectedOptions,
        attributeFace,
        setAttributeFace,
        setOptionFace,
        merchantProductOptions,
        merchantProductOptionsError
    ] = useComposerStore((store) => [
        store.attributes,
        store.sceneMaterialList,
        store.sceneObjectList,
        store.updateAttribute,
        store.updateOption,
        store.removeOption,
        store.cloneOption,
        store.selectOptions,
        store.validateAttributes,
        store.validateOptions,
        store.selectedOptions,
        store.attributeFace,
        store.setAttributeFace,
        store.setOptionFace,
        store.merchantProductOptions,
        store.merchantProductOptionsError
    ]);
    const [zkScene, sceneEditor] = usePreview3DStore((store) => [store.zkScene, store.sceneEditor]);
    const { showDialog, closeDialog } = useDialogManager();
    const { filter, setFilter, selectedIds, filteredData, addNode, findNodeById, updateNode, removeNodes, setNodes, setSelectedIds } = state;

    React.useEffect(() => {
        const optionIds = selectedOptions?.map((option) => option.id) ?? [];
        if (optionIds.join(',') !== selectedIds.join(',')) setSelectedIds(optionIds);
    }, [selectedOptions, setSelectedIds]);

    React.useEffect(() => {
        if (!selectedAttribute) return;
        const firstOption = [...options].sort((left, right) => left.displayOrder - right.displayOrder)[0];
        setSelectedIds(firstOption ? [firstOption.id] : []);
        selectOptions(firstOption ? [firstOption] : []);
    }, [selectedAttribute?.id]);

    const openOptionFace = (option: ComposerAttributeOptionNode, face: 'settings' | 'links') => {
        setSelectedIds([option.id]);
        selectOptions([option]);
        setOptionFace(face);
    };

    const addOption = () => {
        if (!selectedAttribute) return;
        const option = ComposerAttributeNodeAdapter.toNodeOption(selectedAttribute.id, ComposerAttributeNodeAdapter.createNewOption(options.length));
        const names = new Set(options.map((candidate) => candidate.name));
        let suffix = 1;
        let name = T._('New option', 'Admin');
        while (names.has(name)) name = `${T._('New option', 'Admin')} ${++suffix}`;
        const updatedOption = {
            ...option,
            name,
            description: '',
            attributeID: selectedAttribute.id,
            displayOrder: options.length,
            isDefault: options.length === 0,
            isNew: true
        };
        addNode(updatedOption);
        updateOption(updatedOption);
        openOptionFace(updatedOption, 'settings');
        void validateAttributes();
    };

    const removeOptions = (optionsToRemove: ComposerAttributeOptionNode[]) => {
        if (!selectedAttribute || optionsToRemove.length === 0) return;
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={T._('Are you sure you want to remove the selected option(s)? ', 'Admin') + optionsToRemove.map((option) => option.name).join(', ')}
                buttons={[
                    { label: T._('Cancel', 'Admin'), outline: true, onClick: () => closeDialog('confirmDialog') },
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            const ids = optionsToRemove.map((option) => option.id);
                            removeNodes(ids);
                            optionsToRemove.forEach((option) => removeOption(option.attributeID, option.id));
                            setSelectedIds([]);
                            selectOptions([]);
                            void validateAttributes();
                        }
                    }
                ]}
            />
        );
    };

    const retargetOptions = (optionsToRetarget: ComposerAttributeOptionNode[]) => {
        const actions = optionsToRetarget.flatMap((option) => option.actions || []);
        const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
        showDialog(
            'composerRetargetAttribute',
            <ComposerRetargetAttributeDialog
                materialsList={sceneMaterialList}
                objectsList={sceneMeshList}
                usedMaterialsList={sceneMaterialList.filter((material) => materialIds.includes(material.id))}
                usedObjectsList={sceneMeshList.filter((mesh) => objectIds.includes(mesh.id) || objectIds.includes(mesh.name || ''))}
                onConfirm={(data) => {
                    closeDialog('composerRetargetAttribute');
                    if (!data) return;
                    const remapped = retargetMapToActions(actions, data.materialMap, data.objectMap);
                    optionsToRetarget.forEach((option) => {
                        const updatedOption = {
                            ...option,
                            actions: option.actions.map((action) => remapped.find((candidate) => candidate.id === action.id) ?? action)
                        };
                        updateOption(updatedOption);
                        updateNode(updatedOption.id, updatedOption);
                    });
                }}
            />
        );
    };

    const onRowAction = (action: 'edit' | 'links' | 'duplicate' | 'retarget' | 'remove', option: ComposerAttributeOptionNode) => {
        if (action === 'edit') return openOptionFace(option, 'settings');
        if (action === 'links') return openOptionFace(option, 'links');
        if (action === 'duplicate') {
            const cloned = cloneOption(option);
            addNode(cloned);
            setSelectedIds([cloned.id]);
            selectOptions([cloned]);
            setOptionFace('settings');
            return;
        }
        if (action === 'retarget') return retargetOptions([option]);
        removeOptions([option]);
    };

    const toggleOptionEnabled = (option: ComposerAttributeOptionNode) => {
        const updatedOption = { ...option, enabled: option.enabled === false };
        updateOption(updatedOption);
        updateNode(updatedOption.id, updatedOption);
    };

    const handleSetDefaultOption = (option: ComposerAttributeOptionNode) => {
        if (!selectedAttribute) return;
        useComposerStore.setState((store) => {
            const index = store.attributes.findIndex((attribute) => attribute.id === selectedAttribute.id);
            if (index < 0) return store;
            const updatedAttribute = {
                ...store.attributes[index],
                options: store.attributes[index].options.map((candidate) => ({ ...candidate, isDefault: candidate.id === option.id }))
            };
            return {
                ...store,
                attributes: store.attributes.map((attribute, attributeIndex) => (attributeIndex === index ? updatedAttribute : attribute)),
                selectedAttributes: store.selectedAttributes?.map((attribute) => (attribute.id === updatedAttribute.id ? updatedAttribute : attribute)) ?? null,
                selectedOptions: store.selectedOptions?.map((candidate) => ({ ...candidate, isDefault: candidate.id === option.id })) ?? null
            };
        });
    };

    const nodeSelectHandler = (event: React.MouseEvent, option: ComposerAttributeOptionNode) => {
        if (event.shiftKey && selectedIds.length > 0) {
            const visibleOptions = [...filteredData].sort((left, right) => sortByDisplay(filteredData, left, right));
            const startIndex = visibleOptions.findIndex((candidate) => candidate.id === selectedIds[selectedIds.length - 1]);
            const endIndex = visibleOptions.findIndex((candidate) => candidate.id === option.id);
            if (startIndex < 0 || endIndex < 0) return;
            const range = visibleOptions.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1);
            setSelectedIds(range.map((candidate) => candidate.id));
            selectOptions(range);
            return;
        }

        if (event.ctrlKey || event.metaKey) {
            const nextIds = selectedIds.includes(option.id) ? selectedIds.filter((id) => id !== option.id) : [...selectedIds, option.id];
            setSelectedIds(nextIds);
            selectOptions(nextIds.map((id) => findNodeById(id)).filter(Boolean) as ComposerAttributeOptionNode[]);
            return;
        }

        setSelectedIds([option.id]);
        selectOptions([option]);
    };

    const moveNode = (option: ComposerAttributeOptionNode, direction: 'up' | 'down') => {
        const siblings = [...options].sort((left, right) => sortByDisplay(options, left, right));
        const index = siblings.findIndex((candidate) => candidate.id === option.id);
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= siblings.length) return;
        const swap = siblings[swapIndex];
        updateNode(option.id, { ...option, displayOrder: swap.displayOrder });
        updateNode(swap.id, { ...swap, displayOrder: option.displayOrder });
        updateOption({ ...option, displayOrder: swap.displayOrder });
        updateOption({ ...swap, displayOrder: option.displayOrder });
    };

    const dropNode = (event: React.DragEvent<HTMLDivElement>, target: ComposerAttributeOptionNode) => {
        event.preventDefault();
        event.stopPropagation();
        const draggedOptionId = event.dataTransfer.getData(COMPOSER_OPTION_DRAG_TYPE) || event.dataTransfer.getData('text/plain');
        const reordered = reorderComposerOptions(options, draggedOptionId, target.id);
        if (reordered === options) return;
        reordered.forEach((option, displayOrder) => {
            const currentOption = options.find((candidate) => candidate.id === option.id);
            if (currentOption?.displayOrder === displayOrder) return;
            updateNode(option.id, option);
            updateOption(option);
        });
        const movedOption = reordered.find((option) => option.id === draggedOptionId);
        if (movedOption) {
            setSelectedIds([movedOption.id]);
            selectOptions([movedOption]);
        }
    };

    if (!selectedAttribute || multiple) {
        return (
            <ComposerPanel>
                <ComposerEmptyState>{T._('Select one attribute to see its options and settings.', 'Admin')}</ComposerEmptyState>
            </ComposerPanel>
        );
    }

    const nodes = [...filteredData].sort((left, right) => sortByDisplay(filteredData, left, right));
    const activeTab = attributeFace === 'settings' ? 1 : attributeFace === 'links' ? 2 : 0;
    const updateSelectedAttribute = (changes: Partial<ComposerAttributeNode>) => {
        updateAttribute({ ...selectedAttribute, ...changes, isNew: true });
        if (changes.name !== undefined || changes.code !== undefined || Object.prototype.hasOwnProperty.call(changes, 'platformAttribute')) {
            void validateAttributes();
        }
    };
    const updateAttributeOptionImages = async (uploadedImageUrl: string, tempImageFileKey?: string) => {
        const image = createComposerOptionImageReference(uploadedImageUrl, tempImageFileKey);
        const updatedOptions = selectedAttribute.options.map((option) => applyComposerOptionImage(option, image));
        updatedOptions.forEach((option) => {
            updateNode(option.id, option);
            updateOption(option);
        });
        await validateOptions({ ...selectedAttribute, options: updatedOptions });
        return image?.imageUrl ?? '';
    };
    const platformAttributes = [
        { value: '', label: T._('Not connected', 'Admin') },
        ...merchantProductOptions.map((attribute) => ({
            value: attribute.optionID,
            label: attribute.label || attribute.optionID
        }))
    ];
    const changePlatformAttribute = (selection: { value: string; label: string } | null) => {
        const platformAttribute = selection?.value ? { id: selection.value, label: selection.label } : undefined;
        const storeAttribute = platformAttribute ? merchantProductOptions.find((attribute) => attribute.optionID === platformAttribute.id) : undefined;
        const wasStoreAttribute = Boolean(selectedAttribute.platformAttribute?.id) || isComposerStoreCode(selectedAttribute.code);

        const updatedOptions = platformAttribute
            ? (storeAttribute?.values ?? []).map((storeOption, displayOrder) => {
                  const existingOption = selectedAttribute.options.find((option) => option.platformAttribute?.id === storeOption.valueID);
                  const option =
                      existingOption ??
                      ComposerAttributeNodeAdapter.toNodeOption(selectedAttribute.id, ComposerAttributeNodeAdapter.createNewOption(displayOrder));
                  return {
                      ...option,
                      attributeID: selectedAttribute.id,
                      name: storeOption.label || storeOption.valueID,
                      code: JSON.stringify({ id: storeOption.valueID, label: storeOption.label || storeOption.valueID, selected: true }),
                      displayOrder,
                      isDefault: existingOption?.isDefault ?? displayOrder === 0,
                      isNew: existingOption?.isNew ?? true,
                      platformAttribute: { id: storeOption.valueID, label: storeOption.label || storeOption.valueID }
                  };
              })
            : selectedAttribute.options
                  .filter((option) => !option.platformAttribute?.id && !isComposerStoreCode(option.code))
                  .map((option, displayOrder) => ({ ...option, displayOrder }));

        const defaultOption = updatedOptions.find((option) => option.isDefault) ?? updatedOptions[0];
        const normalizedOptions = updatedOptions.map((option) => ({ ...option, isDefault: option.id === defaultOption?.id }));
        const retainedOptionIds = new Set(normalizedOptions.map((option) => option.id));

        setNodes(normalizedOptions);
        setSelectedIds(defaultOption ? [defaultOption.id] : []);
        selectOptions(defaultOption ? [normalizedOptions.find((option) => option.id === defaultOption.id)!] : []);
        updateSelectedAttribute({
            platformAttribute,
            options: normalizedOptions,
            pricings: selectedAttribute.pricings.filter((pricing) => retainedOptionIds.has(pricing.selectedOptionGuid)),
            code: platformAttribute
                ? JSON.stringify({ ...platformAttribute, selected: true })
                : wasStoreAttribute
                  ? createComposerLocalCode(selectedAttribute.name, 'attribute')
                  : selectedAttribute.code
        });
    };
    const cameraOptions =
        zkScene
            ?.get?.('cameraLocations')
            ?.models?.map((location) => ({ value: location.getLocationID(), label: location.get('name') || location.getLocationID() })) ?? [];
    const meshOptions = sceneMeshList.filter((mesh) => sceneEditor?.isRealMesh(mesh)).map((mesh) => ({ value: mesh.id, label: mesh.name || mesh.id }));
    const selectedMeshes = (selectedAttribute.objects || '')
        .split(/[;,]/)
        .filter(Boolean)
        .map((id) => ({ value: id, label: meshOptions.find((option) => option.value === id)?.label || id }));

    return (
        <ComposerPanel aria-label={T._('Attribute options', 'Admin')}>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='attribute'>
                    {selectedAttribute.img ? <img src={selectedAttribute.img} alt='' /> : <AttributeIcon />}
                </ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={T._('Attribute name', 'Admin')}
                        value={selectedAttribute.name}
                        onChange={(event) => updateSelectedAttribute({ name: event.target.value })}
                        onBlur={validateAttributes}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind='attribute'>{T._('Attribute', 'Admin')}</ComposerKindBadge>
                        <span>{T._(`${options.length} options`, 'Admin')}</span>
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
            </ComposerSubjectHeader>
            <ComposerTabs selectedIndex={activeTab} onSelect={(index) => setAttributeFace(index === 1 ? 'settings' : index === 2 ? 'links' : 'options')}>
                <TabList hasOverflow>
                    <Tab>
                        {T._('Options', 'Admin')} <ComposerTabCount>{options.length}</ComposerTabCount>
                    </Tab>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                    <Tab>
                        {T._('Links', 'Admin')} <ComposerTabCount>{selectedAttribute.links.length}</ComposerTabCount>
                    </Tab>
                </TabList>
                <TabPanel>
                    <ComposerToolbar>
                        <ComposerToolbarButton onClick={addOption}>
                            <Plus />
                            {T._('Add', 'Admin')}
                        </ComposerToolbarButton>
                        <ComposerToolbarSpacer />
                        {selectedIds.length > 1 && (
                            <ComposerToolbarButton
                                onClick={() => removeOptions(selectedIds.map((id) => findNodeById(id)).filter(Boolean) as ComposerAttributeOptionNode[])}
                            >
                                {T._(`Remove ${selectedIds.length}`, 'Admin')}
                            </ComposerToolbarButton>
                        )}
                    </ComposerToolbar>
                    <ComposerSearchRow>
                        <ComposerSearch
                            aria-label={T._('Search options', 'Admin')}
                            disabled={options.length === 0}
                            placeholder={T._('Search options…', 'Admin')}
                            type='search'
                            value={filter}
                            onChange={(event) => setFilter(event.target.value)}
                        />
                    </ComposerSearchRow>
                    <ComposerScrollBody>
                        {nodes.length === 0 ? (
                            <ComposerEmptyState>
                                {options.length === 0 ? T._('No options yet. Add the first option.', 'Admin') : T._('No matching options.', 'Admin')}
                            </ComposerEmptyState>
                        ) : (
                            <>
                                <ComposerList role='listbox' aria-multiselectable='true'>
                                    {nodes.map((option) => (
                                        <ComposerOptionTreeElement
                                            key={option.id}
                                            option={option}
                                            selected={selectedIds.includes(option.id)}
                                            hasLink={option.links.length > 0}
                                            onAction={onRowAction}
                                            onDoubleClick={(_, selectedOption) => openOptionFace(selectedOption, 'settings')}
                                            onDragOver={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                event.dataTransfer.dropEffect = 'move';
                                            }}
                                            onDragStart={(event, draggedOption) => {
                                                event.dataTransfer.setData(COMPOSER_OPTION_DRAG_TYPE, draggedOption.id);
                                                event.dataTransfer.setData('text/plain', draggedOption.id);
                                                event.dataTransfer.effectAllowed = 'move';
                                                setSelectedIds([draggedOption.id]);
                                                selectOptions([draggedOption]);
                                            }}
                                            onDrop={(event) => dropNode(event, option)}
                                            onMoveDown={() => moveNode(option, 'down')}
                                            onMoveUp={() => moveNode(option, 'up')}
                                            onSelect={nodeSelectHandler}
                                            onSetDefault={handleSetDefaultOption}
                                            onVisibilityClick={toggleOptionEnabled}
                                        />
                                    ))}
                                </ComposerList>
                                {nodes.length > 1 && (
                                    <ListHint>
                                        {isMacPlatform()
                                            ? T._('⇧click for a range · ⌘click one by one', 'Admin')
                                            : T._('Shift+click for a range · Ctrl/Cmd+click one by one', 'Admin')}
                                    </ListHint>
                                )}
                            </>
                        )}
                    </ComposerScrollBody>
                </TabPanel>
                <TabPanel>
                    <TabBody>
                        <ComposerFormStack>
                            <FormControl
                                label={
                                    <>
                                        {T._('Store attribute (optional)', 'Admin')}
                                        {merchantProductOptionsError && <ComposerValidationTooltip messages={[merchantProductOptionsError]} />}
                                    </>
                                }
                            >
                                <Select
                                    options={platformAttributes}
                                    value={
                                        platformAttributes.find((attribute) => attribute.value === selectedAttribute.platformAttribute?.id) ??
                                        platformAttributes[0]
                                    }
                                    onChange={changePlatformAttribute}
                                    isDisabled={Boolean(merchantProductOptionsError)}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                            <FormControl label={T._('Attribute code', 'Admin')}>
                                {selectedAttribute.platformAttribute?.id ? (
                                    <ComposerDetailValue>
                                        {T._('Managed by the connected store attribute. Disconnect it to edit a local code.', 'Admin')}
                                    </ComposerDetailValue>
                                ) : (
                                    <Input value={selectedAttribute.code} onChange={(event) => updateSelectedAttribute({ code: event.target.value })} />
                                )}
                            </FormControl>
                            <FormControl label={T._('Attribute description', 'Admin')}>
                                <Input
                                    value={selectedAttribute.description}
                                    onChange={(event) => updateSelectedAttribute({ description: event.target.value })}
                                />
                            </FormControl>
                            <FormControl label={T._('Preview options', 'Admin')}>
                                <Select
                                    options={[
                                        { value: ComposerAttributePreviewType.Rectangle, label: T._('Square', 'Admin') },
                                        { value: ComposerAttributePreviewType.Circle, label: T._('Round', 'Admin') }
                                    ]}
                                    value={
                                        selectedAttribute.previewType === ComposerAttributePreviewType.Circle
                                            ? { value: ComposerAttributePreviewType.Circle, label: T._('Round', 'Admin') }
                                            : { value: ComposerAttributePreviewType.Rectangle, label: T._('Square', 'Admin') }
                                    }
                                    onChange={(option) => updateSelectedAttribute({ previewType: option?.value ?? ComposerAttributePreviewType.Rectangle })}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                            <FormControl label={T._('Hide options label', 'Admin')}>
                                <ZSwitch
                                    ariaLabel={T._('Hide options label', 'Admin')}
                                    checked={!!selectedAttribute.hideOptionsLabel}
                                    onChange={(checked) => updateSelectedAttribute({ hideOptionsLabel: checked })}
                                />
                            </FormControl>
                            <FormControl label={T._('Camera position', 'Admin')}>
                                <Select
                                    isClearable
                                    options={cameraOptions}
                                    value={cameraOptions.find((option) => option.value === selectedAttribute.camera) ?? null}
                                    onChange={(option) => updateSelectedAttribute({ camera: option?.value || '' })}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                            <FormControl
                                label={T._('Objects', 'Admin')}
                                tooltipLabelHtml={T._('When you click on these objects the camera will move to the selected position', 'Admin')}
                            >
                                <Select
                                    isMulti
                                    options={meshOptions}
                                    value={selectedMeshes}
                                    onChange={(items) => updateSelectedAttribute({ objects: items.map((item) => item.value).join(';') })}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                            <FormControl label={T._('3D preview images', 'Admin')}>
                                <Composer3DPreviewCapture
                                    materials={sceneMaterialList}
                                    disabled={selectedAttribute.options.length === 0}
                                    helperText={T._('Generate one image from the 3D scene and apply it to every option of this attribute.', 'Admin')}
                                    onImagePick={updateAttributeOptionImages}
                                    successMessage={T._('The 3D preview was assigned to all options of this attribute.', 'Admin')}
                                />
                                {selectedAttribute.options.length === 0 && (
                                    <ComposerUnsupportedNote>
                                        {T._('Add at least one option before generating its preview image.', 'Admin')}
                                    </ComposerUnsupportedNote>
                                )}
                            </FormControl>
                        </ComposerFormStack>
                    </TabBody>
                </TabPanel>
                <TabPanel>
                    <TabBody>
                        <ComposerLinksEditor
                            attributes={attributes}
                            links={selectedAttribute.links}
                            logicalOperator={selectedAttribute.linkOperation}
                            selfAttributeID={selectedAttribute.id}
                            subjectLabel={T._('This attribute', 'Admin')}
                            onChange={(links, linkOperation) => {
                                updateSelectedAttribute({ links, linkOperation });
                                validateAttributes();
                            }}
                        />
                    </TabBody>
                </TabPanel>
            </ComposerTabs>
        </ComposerPanel>
    );
};

export default ComposerOptions;
